/**
 * Which dealership do you want to look at?
 *
 * Shown only to callers who have more than one — see WorkspaceContext, which
 * owns the list and settles the single-dealership case without asking.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './SelectWorkspaceDialog.css';

function SelectWorkspaceDialog({ workspaces, activeSlug, onPick, onCancel }) {
    const firstRef = useRef(null);

    useEffect(() => { firstRef.current?.focus(); }, []);

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') onCancel(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    return (
        // UnitDetailPane closes on any mousedown outside itself and spares only
        // nodes carrying this marker. Without it, opening the picker over an open
        // unit pane pulls the pane out from under the dialog.
        <div
            className="ws-picker-overlay"
            data-pane-overlay="true"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="ws-picker" role="dialog" aria-modal="true" aria-labelledby="ws-picker-title">
                <h2 className="ws-picker-title" id="ws-picker-title">Choose a dealership</h2>
                <p className="ws-picker-sub">Whose inventory do you want to look at?</p>

                <div className="ws-picker-list">
                    {workspaces.map((w, i) => {
                        const isActive = w.slug === activeSlug;
                        return (
                            <button
                                key={w.slug}
                                type="button"
                                ref={i === 0 ? firstRef : null}
                                className={`ws-picker-item${isActive ? ' ws-picker-item-active' : ''}`}
                                onClick={() => onPick(w)}
                            >
                                <span className="ws-picker-mark" aria-hidden="true">
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                        <path fillRule="evenodd" d="M4 3a1 1 0 011-1h6a1 1 0 011 1v14h3v-8a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H2a1 1 0 110-2h1V3zm2 2v2h2V5H6zm4 0v2h-2V5h2zM6 9v2h2V9H6zm4 0v2h-2V9h2z" clipRule="evenodd" />
                                    </svg>
                                </span>
                                <span className="ws-picker-text">
                                    <span className="ws-picker-name">{w.name || w.slug}</span>
                                    <span className="ws-picker-slug">{w.slug}</span>
                                </span>
                                {w.read_only && <span className="ws-picker-badge">Read-only</span>}
                                {isActive && <span className="ws-picker-current">Current</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="ws-picker-actions">
                    <button type="button" className="ws-picker-cancel" onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </div>
    );
}

SelectWorkspaceDialog.propTypes = {
    workspaces: PropTypes.arrayOf(PropTypes.shape({
        slug: PropTypes.string.isRequired,
        name: PropTypes.string,
        read_only: PropTypes.bool,
    })).isRequired,
    activeSlug: PropTypes.string,
    onPick: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default SelectWorkspaceDialog;
